
import React from 'react';
import { MapPin, Music, Baby, Brain, Plane } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";

type PersonalInterest = {
  icon: React.ReactNode;
  emoji: string;
  title: string;
  description: string;
  color: string;
  details: string;
  image?: string;
};

const PersonalSection = () => {
  const interests: PersonalInterest[] = [
    {
      icon: <Plane className="h-10 w-10" />,
      emoji: "🌎",
      title: "Travel Junkie",
      description: "Favorite place: Hawaii - collecting experiences and perspectives around the world.",
      color: "bg-blush-100 text-blush-600",
      details: "I've visited 12 states and 3 countries so far, with a goal to see all 50 states by age 35. Hawaii's natural beauty and cultural richness captured my heart completely. I love traveling with my brothers Reid & Ryan and my sisters Nolan & Summer - they're my favorite travel companions!",
      image: "/lovable-uploads/cf75015a-3b44-4f84-b8b7-00f8a91a4fb2.png"
    },
    {
      icon: <Music className="h-10 w-10" />,
      emoji: "🩰",
      title: "Former Ballerina",
      description: "Led recitals and choreographed shows, bringing creative vision to life.",
      color: "bg-warm-100 text-warm-600",
      details: "Dancing for 12 years taught me discipline, perseverance, and the power of creative expression. I still incorporate movement practices into my daily routine.",
      image: "/lovable-uploads/98fb1fdf-7238-4025-a6ff-e6f1282204ca.png"
    },
    {
      icon: <Baby className="h-10 w-10" />,
      emoji: "👶",
      title: "Amazing with Kids",
      description: "Applied psychology at Boys & Girls Club through meaningful mentorship.",
      color: "bg-sage-100 text-sage-600",
      details: "Working with kids at the Boys & Girls Club was where I first saw how my psychology education could have real impact. Seeing their growth and confidence bloom was incredibly rewarding.",
      image: "/lovable-uploads/0872a738-5aa8-4cd7-8362-c726446e1523.png"
    },
    {
      icon: <Brain className="h-10 w-10" />,
      emoji: "🧠",
      title: "Always Curious",
      description: "Exploring AI, prompt crafting, and systems thinking to solve problems.",
      color: "bg-blush-100 text-blush-600",
      details: "I'm fascinated by how technology can be harnessed to solve human problems. I'm constantly learning about AI tools and how they can enhance both personal and professional workflows.",
      image: "/lovable-uploads/1eec37d2-6dad-4cb3-a219-8a18bef1ada0.png"
    }
  ];
  
  return (
    <section id="personal" className="py-16 px-6">
      <div className="container max-w-5xl mx-auto">
        <div className="mb-12 text-center">
          <h2 className="text-sage-800 mb-2">Who I Am Outside of Work</h2>
          <div className="w-24 h-1 bg-sage-300 mx-auto"></div>
          <p className="mt-4 text-sage-700 max-w-2xl mx-auto">
            The passions and pursuits that make me who I am
          </p>
        </div>
        
        {/* Desktop view */}
        <div className="hidden md:grid grid-cols-1 md:grid-cols-2 gap-6">
          {interests.map((interest, index) => (
            interest.image ? (
              <Dialog key={index}>
                <DialogTrigger asChild>
                  <Card className="border-none shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className={`p-3 rounded-full ${interest.color} flex items-center justify-center`}>
                        <span className="text-2xl mr-2">{interest.emoji}</span>
                        {interest.icon}
                      </div>
                      <div>
                        <h3 className="text-xl font-medium text-gray-800 mb-2">{interest.title}</h3>
                        <p className="text-gray-600">{interest.description}</p>
                        <p className="text-sage-600 mt-2 text-sm">Click to see my journey</p>
                      </div>
                    </CardContent>
                  </Card>
                </DialogTrigger>
                <DialogContent className={interest.title === "Always Curious" ? "sm:max-w-md max-h-[90vh] overflow-auto" : "sm:max-w-md"}>
                  <div className="space-y-4">
                    <h3 className="text-xl font-serif text-center">{interest.title}</h3>
                    <div className={interest.title === "Always Curious" ? "" : ""}>
                      <AspectRatio ratio={interest.title === "Always Curious" ? 4/5 : 1/1} className="bg-muted">
                        <img 
                          src={interest.image}
                          alt={`Image representing ${interest.title}`}
                          className={`rounded-md object-contain w-full h-full`}
                        />
                      </AspectRatio>
                    </div>
                    <p className="text-sm text-center text-muted-foreground">{interest.details}</p>
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              <TooltipProvider key={index}>
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <Card className="border-none shadow-md hover:shadow-lg transition-all duration-300">
                      <CardContent className="p-6 flex items-start gap-4">
                        <div className={`p-3 rounded-full ${interest.color} flex items-center justify-center`}>
                          <span className="text-2xl mr-2">{interest.emoji}</span>
                          {interest.icon}
                        </div>
                        <div>
                          <h3 className="text-xl font-medium text-gray-800 mb-2">{interest.title}</h3>
                          <p className="text-gray-600">{interest.description}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-white shadow-md p-3 max-w-xs">
                    <p className="text-sm">{interest.details}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          ))}
        </div>
        
        {/* Mobile view with tabs */}
        <div className="md:hidden">
          <Tabs defaultValue={interests[0].title.toLowerCase().replace(/\s+/g, '-')}>
            <TabsList className="grid grid-cols-2 mb-6">
              {interests.slice(0, 2).map((interest, index) => (
                <TabsTrigger key={index} value={interest.title.toLowerCase().replace(/\s+/g, '-')}>
                  <span className="mr-2">{interest.emoji}</span>
                  {interest.title}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsList className="grid grid-cols-2 mb-6">
              {interests.slice(2, 4).map((interest, index) => (
                <TabsTrigger key={index} value={interest.title.toLowerCase().replace(/\s+/g, '-')}>
                  <span className="mr-2">{interest.emoji}</span>
                  {interest.title}
                </TabsTrigger>
              ))}
            </TabsList>
            
            {interests.map((interest, index) => (
              <TabsContent key={index} value={interest.title.toLowerCase().replace(/\s+/g, '-')}>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex justify-center mb-4">
                      <div className={`p-4 rounded-full ${interest.color}`}>
                        {interest.icon}
                      </div>
                    </div>
                    <h3 className="text-xl font-medium text-center text-gray-800 mb-3">{interest.title}</h3>
                    <p className="text-gray-600 mb-4">{interest.description}</p>
                    
                    {interest.image ? (
                      <div className="mb-4">
                        <AspectRatio ratio={interest.title === "Always Curious" ? 4/5 : 1/1} className="bg-muted">
                          <img 
                            src={interest.image}
                            alt={`Image representing ${interest.title}`}
                            className="object-contain w-full h-full rounded-md"
                          />
                        </AspectRatio>
                      </div>
                    ) : null}
                    
                    <p className="text-sm text-gray-500 italic">{interest.details}</p>
                  </CardContent>
                </Card>
              </TabsContent>
            ))}
          </Tabs>
        </div>
        
        <div className="mt-12 bg-gradient-to-r from-sage-100 to-warm-100 p-8 rounded-xl shadow-inner text-center">
          <p className="text-lg font-serif italic text-gray-700">
            "I believe that our personal interests and experiences shape how we show up professionally, bringing richness and perspective to everything we do."
          </p>
        </div>
      </div>
    </section>
  );
};

export default PersonalSection;
